
import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { Component, Renderer2, Injector } from '@angular/core';
import { ComponentFactoryResolver, ChangeDetectorRef } from '@angular/core';

import { DynamicBaseComponent } from '../dynamic-base.component';

import * as moment_api from 'moment';
const moment = moment_api;

export interface INotification {
    id: string;                      // Identifier of the notification
    time: number;                    // Time of creation
    data?: { [name: string]: any };  // Data to pass the template
    name?: string;                   // Display name of the notitification action
    action?: () => void;             // Callback for action display interactions
    hide?: boolean;                  // Hide notification
}

@Component({
    selector: 'notification-overlay',
    template: `
<div [class]="'notification ' + model.name" widget >
    <div class="list" *ngIf="model && model.items">
        <div class="notify" *ngFor="let item of model.items | slice:0:8; let i = index" [style.transform]="'translateY(' + -100 * i + '%)'">
            <div [class]="'content ' + item.name || item.data.name" [@show] *ngIf="!item.hide" (touchrelease)="removeItem(item)" (swiperight)="removeItem(item, true)">
                <div class="item">
                    <div class="details">
                        <div *ngIf="!item.data.template" [innerHTML]="(item.html || item.data.html) | safe"></div>
                        <ng-container *ngIf="item.data && item.data.template">
                            <ng-container *ngTemplateOutlet="item.data.template"></ng-container>
                        </ng-container>
                    </div>
                    <div class="action" *ngIf="item.data.action" (touchrelease)="action(item)">{{item.data.action}}</div>
                </div>
            </div>
        </div>
    </div>
</div>
`,
    styles: [`.notification{position:absolute;bottom:0;right:0;z-index:4000;pointer-events:auto}.notify{position:absolute;bottom:.25em;right:.25em;height:4em;-webkit-transition:-webkit-transform .2s;transition:transform .2s;transition:transform .2s,-webkit-transform .2s}.content{position:absolute;top:.25em;bottom:.25em;right:.25em;padding:.25em;display:-webkit-box;display:-ms-flexbox;display:flex;min-width:15em;-webkit-box-align:center;-ms-flex-align:center;align-items:center;color:rgba(0,0,0,.87);background-color:#f0f0f0;-webkit-box-shadow:0 1px 3px 0 rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12);box-shadow:0 1px 3px 0 rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12);border-radius:4px}.item{display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center}.action{font-weight:700;font-size:.8em;padding:.5em 1em;cursor:pointer}.action:hover{opacity:.65}.success{color:#fff;background-color:#4caf50}.error{color:#fff;background-color:#f44336}.warning{color:#fff;background-color:#ff9800}.info{color:#fff;background-color:#2196f3}`],
    animations: [
        trigger('show', [
            transition(':leave', [
                animate(220, keyframes([
                    style({ opacity: 1, transform: 'translateX(0%)', offset: 0 }),
                    style({ opacity: .8, transform: 'translateX(-20%)', offset: .8 }),
                    style({ opacity: 0, transform: 'translateX(100%)', offset: 1 })
                ]))
            ]),
            transition(':enter', [
                animate(220, keyframes([
                    style({ opacity: 0, transform: 'translateY(100%)', offset: 0 }),
                    style({ opacity: .8, transform: 'translateY(-20%)', offset: .8 }),
                    style({ opacity: 1, transform: 'translateY(0%)', offset: 1 })
                ]))
            ]),
        ]),
    ],
})
export class NotificationComponent extends DynamicBaseComponent {
    protected static self: NotificationComponent = null;
    public model: { [name: string]: any, items?: INotification[] };

    /**
     * Adds a new notification to the global component instance
     * @param id ID of notification
     * @param data Data to pass to the notification
     * @param action Action display to show on notification
     * @param cntr Container with which the notification should be displayed
     */
    public static notify(id: string, data: { [name: string]: any }, action?: () => void, cntr: string = 'root') {
        if (NotificationComponent.self) {
            setTimeout(() => NotificationComponent.self.notify(id, data, action), 10);
        }
        return () => { NotificationComponent.dismiss(id, cntr); };
    }
    /**
     * Remove a notification
     * @param id ID of the notification
     * @param cntr Container of the notification layer
     */
    public static dismiss(id: string, cntr: string = 'root') {
        if (NotificationComponent.self) {
            setTimeout(() => NotificationComponent.self.dismiss(id), 10);
        }
    }

    /**
     * Set delay for automatically removing notifications
     * @param delay Number of milliseconds
     */
    public static timeout(delay: number) {
        if (NotificationComponent.self) {
            NotificationComponent.self.delay(delay);
        }
    }

    protected type = 'Notify';

    constructor(private injector: Injector) {
        super();
        this._cfr = this.injector.get(ComponentFactoryResolver);
        this._cdr = this.injector.get(ChangeDetectorRef);
        this.renderer = this.injector.get(Renderer2);
        NotificationComponent.self = this;
        this.model.timeout = 30 * 1000;
    }

    public resize() {
        setTimeout(() => {
            const el = this.model.el;
            if (el && el.nativeElement) {
                this.cntr_box = el.nativeElement.getBoundingClientRect();
            }
        }, 100);
    }

    protected update(data: { [name: string]: any }) {
        this.resize();
        super.update(data);
    }

    public delay(delay: number) {
        this.model.timeout = delay;
    }

    public notify(id: string, data: { [name: string]: any }, action?: () => void) {
        if (!this.model.items) {
            this.model.items = [];
        }
        this.model.items.unshift({
            id,
            time: moment().valueOf(),
            data: data,
            name: data.name,
            action
        });
        if (data.timeout || this.model.timeout) {
            setTimeout(() => this.dismiss(id), data.timeout || this.model.timeout);
        }
    }

    public dismiss(id: string) {
        if (this.model.items) {
            for (const item of this.model.items) {
                if (item.id === id) {
                    item.hide = true;
                    setTimeout(() => {
                        this.model.items.splice(this.model.items.indexOf(item), 1);
                        if (item.data && item.data.dismiss && item.data.dismiss instanceof Function) {
                            item.data.dismiss();
                        }
                    }, 300);
                    break;
                }
            }
        }
    }

    public action(item: any) {
        if (item.action && item.action instanceof Function) {
            item.action();
            this.dismiss(item.id);
        }
    }

    public removeItem(item: any, force: boolean = false) {
        if (!item.action || force) {
            this.dismiss(item.id);
        }
    }

    protected render() { return; }
}
